<template>
  <AppShell :hero-mode="!isLoggedIn">
    <!-- Guest hero -->
    <PageHero
      v-if="!isLoggedIn"
      eyebrow="Premium"
      icon="fas fa-crown"
      title="Upgrade to Premium"
      subtitle="Unlock all exclusive modules, 500 AI mentor messages/week, and 50 machines/week. Accelerate your path to mastery."
      cta="Create Free Account"
      cta-href="/register"
    />

    <div :class="isLoggedIn ? '' : 'container-app py-10'">
    <!-- Compact heading (authed) -->
    <div v-if="isLoggedIn" class="text-center max-w-2xl mx-auto mb-12">
      <p class="eyebrow justify-center"><i class="fas fa-crown text-yellow-400"></i> Premium</p>
      <h1 class="mt-3 text-3xl sm:text-4xl font-extrabold text-white">Upgrade to <span class="gradient-text">Premium</span></h1>
      <p class="mt-3 text-ink-muted">Unlock the full platform and accelerate your path to mastery.</p>
    </div>

    <!-- Benefits -->
    <div class="card p-6 sm:p-8 mb-10 grid sm:grid-cols-2 gap-4">
      <div v-for="benefit in benefits" :key="benefit" class="flex items-start gap-3">
        <i class="fas fa-circle-check text-success mt-0.5"></i>
        <p class="text-sm text-ink" v-html="benefit"></p>
      </div>
    </div>

    <!-- Plans -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <div
        v-for="plan in plans"
        :key="plan.type"
        :class="[
          'card card-hover p-6 text-center relative flex flex-col',
          plan.popular ? 'border-brand/60 shadow-glow-sm' : '',
        ]"
      >
        <span v-if="plan.popular" class="badge absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-gradient text-white">
          <i class="fas fa-star"></i> Best Value
        </span>
        <img :src="plan.img" :alt="plan.label" class="h-24 mx-auto mb-4 object-contain" />
        <h2 class="text-lg font-bold text-white mb-1">{{ plan.label }}</h2>
        <p class="text-sm text-ink-faint line-through">{{ plan.was }}</p>
        <p class="text-2xl font-extrabold gradient-text mb-1">{{ plan.now }}</p>
        <p class="text-xs text-ink-muted mb-6 flex-1">{{ plan.per }}</p>
        <button
          @click="subscribeAndPay(plan.type)"
          :class="plan.popular ? 'btn-primary w-full justify-center' : 'btn-secondary w-full justify-center'"
        >
          Subscribe Now
        </button>
      </div>
    </div>
    </div>
  </AppShell>
</template>

<script>
import AppShell from "@/components/ui-components/AppShell.vue";
import PageHero from "@/components/ui-components/PageHero.vue";
import apiClient from "@/axiosConfig";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { isAuthenticated, redirectToLogin } from "@/utils/auth";

export default {
  name: "SubscribePage",
  components: { AppShell, PageHero },
  computed: {
    isLoggedIn() {
      return isAuthenticated();
    },
  },
  data() {
    return {
      benefits: [
        "Full access to <strong class='text-white'>all exclusive modules</strong>",
        "Chat with our AI mentor up to <span class='text-brand font-semibold'>500 messages/week</span> (from 50)",
        "Machine lab access up to <span class='text-brand font-semibold'>50 machines/week</span> (from 15)",
        "More surprises coming <span class='text-brand font-semibold'>(future release)</span>",
      ],
      plans: [
        { type: "monthly", label: "Monthly", was: "Rp 500,000", now: "Rp 35,000", per: "per month", img: require("@/assets/package/monthly.webp"), popular: false },
        { type: "quarterly", label: "3 Months", was: "Rp 1,200,000", now: "Rp 100,000", per: "per 3 months", img: require("@/assets/package/quarter.webp"), popular: false },
        { type: "semiannually", label: "6 Months", was: "Rp 2,300,000", now: "Rp 180,000", per: "per 6 months", img: require("@/assets/package/semianually.webp"), popular: false },
        { type: "annually", label: "Annually", was: "Rp 4,500,000", now: "Rp 300,000", per: "per year", img: require("@/assets/package/yearly.webp"), popular: true },
      ],
    };
  },
  methods: {
    calculateAmount(type) {
      const rates = { monthly: 35000, quarterly: 100000, semiannually: 180000, annually: 300000 };
      return rates[type] || null;
    },
    async subscribeAndPay(subscriptionType) {
      if (!isAuthenticated()) {
        toastr.warning("Please login to subscribe to premium plans", "Login Required");
        redirectToLogin(this.$router, this.$route.fullPath);
        return;
      }
      try {
        const response = await apiClient.post("/subscription/create", { subscriptionType });
        const billingId = response.data.billingId;
        if (billingId) {
          this.$router.push(`/invoice/${billingId}`);
        } else {
          toastr.error("Failed to generate the invoice.", "Error");
        }
      } catch (error) {
        console.error("Error subscribing:", error);
        toastr.error(error.response?.data?.message || "An error occurred while processing your request.", "Error");
      }
    },
    launchMidtransSnap(transactionToken) {
      window.snap.pay(transactionToken, {
        onSuccess: (result) => { console.log(result); toastr.success("Payment successful! Thank you for subscribing.", "Success"); },
        onPending: (result) => { console.log(result); toastr.warning("Payment is pending. Please complete the payment process.", "Pending"); },
        onError: (result) => { console.log(result); toastr.error("Payment failed. Please try again.", "Error"); },
      });
    },
  },
};
</script>
